/**
 * StatusPill — a short status word on a tinted ground (design system). Tones:
 * `info` in progress, `warning` needs attention, `success` done or paid,
 * `danger` failed, `neutral` a kind or tag (Business, All stores). Pass
 * `color` instead to follow an existing status palette (work order states):
 * the text takes the color, the ground a 15% tint of it. The word always
 * carries the meaning.
 */
import React from 'react';
import { textRole, tokenVars, withAlpha } from '@turn-connect/ui';

export type StatusTone = 'info' | 'warning' | 'success' | 'danger' | 'neutral';

export interface StatusPillProps {
  tone?: StatusTone;
  /** A status color (overrides tone). */
  color?: string;
  testId?: string;
  /** The full words, shown on hover when a narrow cell cuts the pill short. */
  title?: string;
  children: React.ReactNode;
}

const TONES: Record<StatusTone, { ground: string; text: string; border?: string }> = {
  info: { ground: tokenVars.infoSubtle, text: tokenVars.text.primary },
  warning: { ground: tokenVars.warningSubtle, text: tokenVars.warningText },
  success: { ground: tokenVars.successSubtle, text: tokenVars.text.primary },
  danger: { ground: tokenVars.dangerSubtle, text: tokenVars.dangerText },
  neutral: { ground: tokenVars.surfaceMuted, text: tokenVars.text.secondary, border: tokenVars.border },
};

export const StatusPill: React.FC<StatusPillProps> = ({ tone = 'neutral', color, testId, title, children }) => {
  const t = color ? { ground: withAlpha(color, 0.15), text: color, border: undefined } : TONES[tone];
  return (
    <span
      data-testid={testId}
      title={title}
      style={{
        ...textRole('pill', t.text),
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 8px',
        borderRadius: 999,
        background: t.ground,
        border: t.border ? `1px solid ${t.border}` : undefined,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        // Never wider than its cell: a long status ends in an ellipsis instead of spilling into the next column.
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    </span>
  );
};

export default StatusPill;
